import { useEffect, useMemo, useRef } from 'react'

import { useMatches } from '@tanstack/react-router'

import { useAdminRedeemable } from '@repo/common/queries/admin/redeem'

export type RedeemableContextResult = {
  redeemableId: string | null
  redemptionId: string | null
  isInRedeemableContext: boolean
  redeemableData: ReturnType<typeof useAdminRedeemable>['data']
  isLoadingRedeemable: boolean
}

const REDEEM_ROOT_PATHS = /^\/redeem(\/buckets)?\/?$/

export function useRedeemableContext(): RedeemableContextResult {
  const matches = useMatches()
  const currentPath =
    matches.length > 0 ? matches[matches.length - 1]!.pathname : '/'
  const pathRedeemableId = useMemo(() => {
    const match = currentPath.match(/^\/redeem\/(?!liquidity|buckets)([^/]+)/)
    return match?.[1] || null
  }, [currentPath])
  const redemptionId = useMemo(() => {
    const match = currentPath.match(
      /^\/redeem\/(?!liquidity|buckets)[^/]+\/(?!underlying|liquidity|fees)([^/]+)/,
    )
    return match?.[1] || null
  }, [currentPath])

  const stickyRedeemableId = useRef<string | null>(null)

  useEffect(() => {
    if (pathRedeemableId) {
      stickyRedeemableId.current = pathRedeemableId
    } else if (!REDEEM_ROOT_PATHS.test(currentPath)) {
      stickyRedeemableId.current = null
    }
  }, [pathRedeemableId, currentPath])

  const redeemableId = pathRedeemableId ?? stickyRedeemableId.current
  const isInRedeemableContext = Boolean(redeemableId)
  const redeemableQuery = useAdminRedeemable(redeemableId || '')
  return {
    redeemableId,
    redemptionId,
    isInRedeemableContext,
    redeemableData: redeemableQuery.data,
    isLoadingRedeemable: redeemableQuery.isLoading,
  }
}
